import { StickyNote } from "lucide-react";
import { useState } from "react";
import { RecipeData } from "../../../../api/recipeSimulator";
import { RecipeTypeClassification } from "../../../../config/enums/recipeSimulator.enum";
import { Badge } from "../../../../components/ui/common/badge";
import NewRecipeDialog from "../../../../components/recipeSimulator/NewRecipeDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../components/ui/common/tooltip";

interface RecipeVariantTitleHeaderProps {
  recipeId: string;
  recipeData: RecipeData | null | undefined;
  isViewOnly?: boolean;
  onRecipeUpdated?: () => void;
}

const RecipeVariantTitleHeader = ({
  recipeId,
  recipeData,
  isViewOnly = false,
  onRecipeUpdated
}: RecipeVariantTitleHeaderProps) => {
  const [notesOpen, setNotesOpen] = useState(false);

  const displayTitle = recipeData?.recipeName || "New Recipe";
  const demoKitDetails = recipeData?.demoKitDetails;
  const isDemoKit =
    recipeData?.recipeTypeClassification?.toUpperCase() === RecipeTypeClassification.DEMOKIT;
  const isPublic = recipeData?.recipeTypeClassification === RecipeTypeClassification.PUBLIC;
  const demoKitRemovedDate = demoKitDetails?.demoKitDeletedAt
    ? new Date(demoKitDetails.demoKitDeletedAt).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      })
    : undefined;

  return (
    <div className="tw-mb-6">
      <div>
        <div className="tw-flex tw-items-center tw-gap-3">
          {isViewOnly ? (
            <h1 className="tw-text-2xl tw-font-display tw-font-bold tw-text-foreground">
              {displayTitle}
            </h1>
          ) : (
            <div className="tw-flex tw-items-center tw-gap-2">
              <h1 className="tw-text-2xl tw-font-display tw-font-bold tw-text-foreground">
                {displayTitle}
              </h1>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setNotesOpen(true)}
                      className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-primary hover:tw-text-primary/80"
                      aria-label="View / update recipe notes"
                    >
                      <StickyNote className="tw-h-4 tw-w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">View / update recipe notes</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          {isDemoKit && (
            <Badge variant="secondary" className="tw-text-xs">
              Demo Kit
            </Badge>
          )}
          {isPublic && (
            <Badge variant="outline" className="tw-text-xs">
              Public
            </Badge>
          )}
        </div>
        {demoKitDetails?.demoKitName && (
          <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
            <span className="tw-text-xs tw-font-normal tw-text-muted-foreground">
              based on {demoKitDetails.demoKitName}
            </span>
            {demoKitDetails.isDemoKitDeleted && (
              <Badge variant="destructive" className="tw-text-[10px]">
                {demoKitRemovedDate ? `removed on ${demoKitRemovedDate}` : "removed"}
              </Badge>
            )}
          </div>
        )}
      </div>
      {!isViewOnly && (
        <NewRecipeDialog
          open={notesOpen}
          onOpenChange={setNotesOpen}
          editRecipe={{ recipeId, recipeData }}
          onSuccess={onRecipeUpdated}
        />
      )}
    </div>
  );
};

export default RecipeVariantTitleHeader;
